/** Extract a YouTube video id from a URL or a bare id. Returns null if none. */
export function parseYouTubeId(input: string): string | null {
  const s = input.trim();
  if (!s) return null;
  if (/^[a-zA-Z0-9_-]{11}$/.test(s)) return s; // already an id

  try {
    const url = new URL(s);
    const host = url.hostname.replace(/^www\./, "");
    if (host === "youtu.be") {
      const id = url.pathname.slice(1).split("/")[0];
      return /^[a-zA-Z0-9_-]{11}$/.test(id) ? id : null;
    }
    if (host.endsWith("youtube.com") || host.endsWith("youtube-nocookie.com")) {
      if (url.pathname === "/watch") {
        const v = url.searchParams.get("v");
        return v && /^[a-zA-Z0-9_-]{11}$/.test(v) ? v : null;
      }
      const m = url.pathname.match(/^\/(?:embed|shorts|v)\/([a-zA-Z0-9_-]{11})/);
      if (m) return m[1];
    }
  } catch {
    return null;
  }
  return null;
}

/** Build the privacy-enhanced embed URL for fullscreen, muted autoplay. */
export function youtubeEmbedUrl(videoId: string, opts: { loop?: boolean } = {}): string {
  const params = new URLSearchParams({
    autoplay: "1",
    mute: "1",
    controls: "0",
    modestbranding: "1",
    rel: "0",
    playsinline: "1",
    enablejsapi: "1",
    iv_load_policy: "3",
    // Show English closed captions at the bottom when the video has them.
    cc_load_policy: "1",
    cc_lang_pref: "en",
    hl: "en",
  });
  if (opts.loop) {
    params.set("loop", "1");
    params.set("playlist", videoId);
  }
  return `https://www.youtube-nocookie.com/embed/${videoId}?${params.toString()}`;
}

/* ----------------------- YouTube IFrame Player API ------------------------ */

// Minimal typings for the bits of the IFrame API we use.
export interface YTPlayer {
  playVideo(): void;
  pauseVideo(): void;
  stopVideo(): void;
  seekTo(seconds: number, allowSeekAhead?: boolean): void;
  destroy(): void;
  loadModule(name: string): void;
  setOption(module: string, option: string, value: unknown): void;
}

export interface YTNamespace {
  Player: new (
    el: HTMLElement | string,
    opts: {
      videoId?: string;
      playerVars?: Record<string, string | number>;
      events?: {
        onReady?: (e: { target: YTPlayer }) => void;
        onStateChange?: (e: { data: number; target: YTPlayer }) => void;
        onError?: (e: { data: number; target: YTPlayer }) => void;
      };
    },
  ) => YTPlayer;
  PlayerState: { ENDED: number; PLAYING: number; PAUSED: number };
}

declare global {
  interface Window {
    YT?: YTNamespace;
    onYouTubeIframeAPIReady?: () => void;
  }
}

/** After a failed load, calls within this window fail fast instead of adding
 *  another script tag — an offline kiosk would otherwise retry on every slide. */
export const YT_RETRY_COOLDOWN_MS = 60_000;

/** How long a script load may take before it counts as failed. */
const LOAD_TIMEOUT_MS = 20_000;

let apiPromise: Promise<YTNamespace> | null = null;
let lastFailureAt: number | null = null;

/** Lazily load the YouTube IFrame API (once) and resolve with the YT namespace.
 *  A failed load is NOT cached forever: on a kiosk that boots before its
 *  network is up, a YouTube slide after the cooldown must retry, not inherit
 *  a rejection until the nightly reload. */
export function loadYouTubeApi(): Promise<YTNamespace> {
  if (apiPromise) return apiPromise;
  if (lastFailureAt !== null && Date.now() - lastFailureAt < YT_RETRY_COOLDOWN_MS) {
    return Promise.reject(new Error("YT failed recently"));
  }
  const pending = new Promise<YTNamespace>((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("no window"));
      return;
    }
    if (window.YT?.Player) {
      resolve(window.YT);
      return;
    }
    const tag = document.createElement("script");
    const fail = (message: string) => {
      clearTimeout(timeout);
      tag.remove(); // so a retry can append a fresh tag
      reject(new Error(message));
    };
    // The script can load yet never call onYouTubeIframeAPIReady (a truncated
    // or blocked download); without a deadline this promise would hang until
    // the nightly reload and no YouTube slide could ever retry.
    const timeout = setTimeout(() => fail("YT load timed out"), LOAD_TIMEOUT_MS);
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      prev?.();
      if (window.YT) {
        clearTimeout(timeout);
        resolve(window.YT);
      } else {
        fail("YT failed to load");
      }
    };
    tag.src = "https://www.youtube.com/iframe_api";
    tag.onerror = () => fail("YT script failed");
    document.head.appendChild(tag);
  });
  apiPromise = pending;
  pending.then(
    () => {
      lastFailureAt = null;
    },
    () => {
      lastFailureAt = Date.now();
      if (apiPromise === pending) apiPromise = null;
    },
  );
  return pending;
}
